// =============================================================================
// VandieZ admin-web -- Promotions index (/promotions)
// =============================================================================
// Top-level list of every promotion program (launch_promo,
// driver_of_month, posting_of_month, referral_*, ...). Rendered as a
// table with at-a-glance health columns the ops team uses to decide
// which program needs attention this month:
//
//   Program             — code, links to /promotions/:code detail page
//   Status              — active / paused pill
//   Prize               — configured prize_amount (per program kind)
//   This month count    — awards already settled in the current period
//   YTD spend           — sum of settled amounts year-to-date
//
// Backed by GET /admin/promotions (promotionsAdminApi.list). The "Export
// YTD CSV" button is a plain anchor to the backend's streaming export
// endpoint — the browser handles the download, no client-side blob
// assembly needed.
//
// (Spec 7, Task 30.)
// =============================================================================

import { AlertCircle, Download, Gift } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { promotionsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { ApiError } from "@/types/auth";
import type { PromotionProgramSummary } from "@/types/promotion";

function statusPillClass(isActive: boolean): string {
  return isActive
    ? "bg-success-subtle text-success-subtle-foreground"
    : "bg-muted text-muted-foreground";
}

export function PromotionsIndexPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [programs, setPrograms] = useState<PromotionProgramSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPrograms = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await promotionsAdminApi.list(accessToken, signal);
        setPrograms(res.programs);
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
          toast.error(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // unmount — silent
        } else {
          const msg = "Unexpected error loading promotion programs.";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, toast],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPrograms(controller.signal);
    return () => { controller.abort(); };
  }, [fetchPrograms]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Promotions"
        description="Manage launch, driver-of-month, posting-of-month, and referral programs. Click a row to configure or settle awards."
        actions={
          <a
            href="/api/v1/admin/promotions/export.csv"
            target="_blank"
            rel="noreferrer"
          >
            <Button variant="outline">
              <Download className="mr-1 h-4 w-4" />
              Export YTD CSV
            </Button>
          </a>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-success" />
            Programs
          </CardTitle>
          <CardDescription>
            {loading
              ? "Loading…"
              : `${programs.length} program${programs.length === 1 ? "" : "s"} configured.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error !== null && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && programs.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              Loading...
            </div>
          ) : programs.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No promotion programs configured yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">Program</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4 text-right">Prize</th>
                    <th className="py-2 pr-4 text-right">This month</th>
                    <th className="py-2 pr-4 text-right">YTD spend</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b last:border-b-0 hover:bg-muted/40"
                    >
                      <td className="py-3 pr-4 font-medium">
                        <Link
                          to={`/promotions/${p.code}`}
                          className="text-primary hover:underline"
                        >
                          {p.code}
                        </Link>
                        {p.kind !== p.code && (
                          <div className="text-xs text-muted-foreground">
                            {p.kind}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(p.is_active)}`}
                        >
                          {p.is_active ? "Active" : "Paused"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {p.prize_amount}
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {p.this_month_count}
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {p.ytd_amount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PromotionsIndexPage;
